// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// `localStorage`, safely (M3-35, M3-37, M3-39).
//
// One wrapper, because there were about to be three. The tier choice, the theme and the
// first-run hints all want the same six lines, and all three want them for the same reason:
// **`localStorage` is not reliably there and not reliably usable.** It is absent in a worker;
// present but throwing on `setItem` in Safari's private browsing; present but throwing on *read*
// behind "block all cookies"; and present but full, which throws a quota error on a value of
// eleven characters if some other tab has filled it.
//
// The rule every caller follows: **a preference that cannot be saved is a small annoyance, and a
// page that will not render because it could not save one is not.** So everything here answers
// with an absence rather than an exception, and nothing above it has a failure path to write.
//
// M3-39 builds the real persistence layer — scores, replays, a resumable game — on IndexedDB,
// which is a different shape and a different set of failures. This stays what it is: the small
// synchronous key-value store that settings live in.

/** The three methods this product uses, and no more. */
export interface StorageLike {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

/**
 * The browser's `localStorage`, if it is there **and works**.
 *
 * Proven rather than trusted: Safari's private mode hands out a `localStorage` object whose
 * `setItem` throws, so `typeof localStorage !== 'undefined'` is not the question. A round trip on
 * a throwaway key is.
 */
export function safeStorage(probeKey = 'hcc.pinball.probe'): StorageLike | undefined {
  try {
    const store = (globalThis as { localStorage?: StorageLike }).localStorage
    if (store === undefined || store === null) return undefined
    store.setItem(probeKey, '1')
    store.removeItem(probeKey)
    return store
  } catch {
    return undefined
  }
}

/** Read a key, or nothing. Never throws. */
export function readLocal(storage: StorageLike | undefined, key: string): string | undefined {
  try {
    return storage?.getItem(key) ?? undefined
  } catch {
    return undefined
  }
}

/** Write a key. Never throws, and says whether it stuck. */
export function writeLocal(
  storage: StorageLike | undefined,
  key: string,
  value: string,
): boolean {
  try {
    storage?.setItem(key, value)
    return storage !== undefined
  } catch {
    return false
  }
}

/** Remove a key. Never throws. */
export function removeLocal(storage: StorageLike | undefined, key: string): void {
  try {
    storage?.removeItem(key)
  } catch {
    // Same reasoning as everywhere else in this file.
  }
}
